import type React from 'react';
import { useEffect, useRef } from 'react';
import { useMapbox } from '@/contexts/MapboxContext';
import { useCdcStore } from '../stores/useCdcStore';

const CDC_SELECTED_SOURCE = 'cdc-selected-vessel';
const CDC_PULSE_LAYERS = ['cdc-selected-pulse-1', 'cdc-selected-pulse-2', 'cdc-selected-pulse-3'];

/**
 * CdcSelectedVesselHighlight - Adds pulsating rings animation around the selected vessel
 * Creates a radar-pulse effect with three expanding white rings
 */
export const CdcSelectedVesselHighlight: React.FC = () => {
  const { map, isMapReady } = useMapbox();
  const selectedMmsi = useCdcStore((state) => state.selectedMmsi);
  const selectedVesselCoords = useCdcStore((state) => state.selectedVesselCoords);

  const animationRef = useRef<number | null>(null);
  const phaseRef = useRef(0);
  const layersAddedRef = useRef(false);

  // Add source and layers
  useEffect(() => {
    if (!isMapReady || !map.current || layersAddedRef.current) return;

    const mapInstance = map.current;

    // Add source for selected vessel point
    if (!mapInstance.getSource(CDC_SELECTED_SOURCE)) {
      mapInstance.addSource(CDC_SELECTED_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    // Add three pulse ring layers
    CDC_PULSE_LAYERS.forEach((layerId) => {
      if (!mapInstance.getLayer(layerId)) {
        mapInstance.addLayer({
          id: layerId,
          type: 'circle',
          source: CDC_SELECTED_SOURCE,
          paint: {
            'circle-radius': 0,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
            'circle-color': 'transparent',
            'circle-stroke-opacity': 0,
          },
        });
      }
    });

    layersAddedRef.current = true;

    // Cleanup on unmount
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }

      if (!map.current) return;
      const m = map.current;

      try {
        CDC_PULSE_LAYERS.forEach((layerId) => {
          if (m.getLayer(layerId)) m.removeLayer(layerId);
        });
        if (m.getSource(CDC_SELECTED_SOURCE)) {
          m.removeSource(CDC_SELECTED_SOURCE);
        }
      } catch {
        // Map may be destroyed
      }

      layersAddedRef.current = false;
    };
  }, [isMapReady, map]);

  // Update selected vessel source when selection or coordinates change
  useEffect(() => {
    if (!map.current || !layersAddedRef.current) return;

    const mapInstance = map.current;
    const source = mapInstance.getSource(CDC_SELECTED_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    if (!source) return;

    if (!selectedMmsi || !selectedVesselCoords) {
      // Clear selection
      source.setData({ type: 'FeatureCollection', features: [] });
      return;
    }

    // Set point at vessel location using coords from map data (not separate API fetch)
    source.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [selectedVesselCoords.lon, selectedVesselCoords.lat],
          },
          properties: { mmsi: selectedMmsi },
        },
      ],
    });
  }, [map, selectedMmsi, selectedVesselCoords]);

  // Animation loop for pulsating rings — only runs when a vessel is selected
  useEffect(() => {
    if (!isMapReady || !map.current || !layersAddedRef.current || !selectedMmsi) return;

    const mapInstance = map.current;

    const animate = () => {
      if (!mapInstance) return;

      // Increment phase (90 frames = ~3 second cycle at 60fps)
      phaseRef.current = (phaseRef.current + 1) % 90;

      const zoom = mapInstance.getZoom();

      // Base radius matches vessel marker size
      const baseRadius = zoom < 9 ? 4 : zoom < 12 ? 8 : 12;

      // Animate each ring with offset timing
      CDC_PULSE_LAYERS.forEach((layerId, i) => {
        if (mapInstance.getLayer(layerId)) {
          // Offset each ring's timing by 30 frames (1/3 of cycle)
          const bandPhase = (phaseRef.current + i * 30) % 90;
          const t = bandPhase / 90; // 0 to 1

          // Expand from marker edge to ~50px beyond
          const radius = baseRadius + t * 50;

          // Fade out as it expands
          const opacity = (1.0 - t) * 0.8;

          try {
            mapInstance.setPaintProperty(layerId, 'circle-radius', radius);
            mapInstance.setPaintProperty(layerId, 'circle-stroke-opacity', opacity);
          } catch {
            // Layer may not exist
          }
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    // Start animation
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isMapReady, map, selectedMmsi]);

  return null;
};
